import { type FormEvent, useCallback, useMemo, useState } from "react";
import { config } from "../config";

type ForgotStep = "email" | "code";

export function ForgotPasswordPage() {
  const [step, setStep] = useState<ForgotStep>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const passwordRequirements = useMemo(
    () => [
      { label: "At least 12 characters", met: newPassword.length >= 12 },
      { label: "One uppercase letter (A–Z)", met: /[A-Z]/.test(newPassword) },
      { label: "One lowercase letter (a–z)", met: /[a-z]/.test(newPassword) },
      { label: "One number (0–9)", met: /[0-9]/.test(newPassword) },
      { label: "One special character (!@#$%^&*)", met: /[!@#$%^&*]/.test(newPassword) },
    ],
    [newPassword],
  );

  const allRequirementsMet = passwordRequirements.every((r) => r.met);

  const handleEmailSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError(null);
      setIsLoading(true);

      try {
        const res = await fetch(`${config.apiBaseUrl}/v1/auth/forgot-password`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        });

        if (res.status === 429) {
          const data = await res.json();
          setError(data.error ?? "Too many requests. Please try again later.");
          return;
        }

        // Always advance to code step (success or not — don't reveal if email exists)
        setStep("code");
      } catch {
        setError("Something went wrong. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
    [email],
  );

  const handleCodeSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError(null);
      setIsLoading(true);

      try {
        const res = await fetch(`${config.apiBaseUrl}/v1/auth/confirm-forgot-password`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, code, newPassword }),
        });

        const data = await res.json();

        if (!res.ok) {
          setError(data.error ?? "Failed to reset password.");
          setIsLoading(false);
          return;
        }

        // Success — redirect to login with a success message in URL
        window.location.href = "/?reset=success";
      } catch {
        setError("Something went wrong. Please try again.");
        setIsLoading(false);
      }
    },
    [email, code, newPassword],
  );

  return (
    <>
      {isLoading && step === "code" && (
        <div className="fullscreen-overlay">
          <div className="loading-spinner" />
          <p>Resetting your password...</p>
        </div>
      )}
      <div className="auth-shell">
        <aside className="auth-brand">
          <img src="/agentech-white-logo.svg" alt="Agentech" className="auth-brand-logo" />

          <div className="auth-brand-content">
            <span className="auth-brand-eyebrow">
              <span className="auth-brand-eyebrow-dot" />
              Account recovery
            </span>
            <h2 className="auth-brand-title">Let's get you back in.</h2>
            <p className="auth-brand-subtitle">Reset your password in three quick steps.</p>
            <ol className="auth-brand-features">
              <li>Verify your email address</li>
              <li>Enter the code we send you</li>
              <li>Choose a new password</li>
            </ol>
          </div>

          <div className="auth-brand-footer">© {new Date().getFullYear()} Agentech</div>
        </aside>

        <main className="auth-main">
          <div className="auth-main-topbar">
            <img src="/agentech-white-logo.svg" alt="Agentech" className="auth-brand-logo" />
          </div>

          <div className="auth-main-body">
            <div className="auth-card">
              <div className="login-header">
                <h1>Reset password</h1>
                <p>
                  {step === "email"
                    ? "Enter your email and we'll send a reset code."
                    : "Enter the code from your email and choose a new password."}
                </p>
              </div>

              {step === "email" && (
                <form onSubmit={handleEmailSubmit} className="login-form">
                  <div className="form-group">
                    <label htmlFor="email">Email address</label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                      disabled={isLoading}
                      autoComplete="email"
                      // biome-ignore lint/a11y/noAutofocus: primary input
                      autoFocus
                    />
                  </div>

                  {error && <div className="error-message">{error}</div>}

                  <button type="submit" className="btn btn-primary" disabled={isLoading}>
                    {isLoading ? <span className="btn-spinner" /> : "Send Reset Code"}
                  </button>

                  <div className="forgot-password">
                    <a href="/">Back to Sign In</a>
                  </div>
                </form>
              )}

              {step === "code" && (
                <form onSubmit={handleCodeSubmit} className="login-form">
                  <div className="form-group">
                    <label htmlFor="code">Verification Code</label>
                    <input
                      id="code"
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="Enter your verification code"
                      required
                      disabled={isLoading}
                      autoComplete="one-time-code"
                      // biome-ignore lint/a11y/noAutofocus: primary input for this step
                      autoFocus
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="new-password">New Password</label>
                    <input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter your new password"
                      required
                      disabled={isLoading}
                      autoComplete="new-password"
                    />
                  </div>

                  <ul className="password-req-list">
                    {passwordRequirements.map((req) => (
                      <li key={req.label} className={`password-req-item${req.met ? " met" : ""}`}>
                        <span className="password-req-icon">{req.met ? "✓" : "○"}</span>
                        {req.label}
                      </li>
                    ))}
                  </ul>

                  <div className="form-group">
                    <label htmlFor="confirm-password">Confirm Password</label>
                    <input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your new password"
                      required
                      disabled={isLoading}
                      autoComplete="new-password"
                    />
                    {confirmPassword && newPassword !== confirmPassword && (
                      <p className="field-error">Passwords do not match</p>
                    )}
                  </div>

                  {error && <div className="error-message">{error}</div>}

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={
                      isLoading ||
                      !code ||
                      !allRequirementsMet ||
                      !confirmPassword ||
                      newPassword !== confirmPassword
                    }
                  >
                    {isLoading ? <span className="btn-spinner" /> : "Reset Password"}
                  </button>

                  <div className="forgot-password">
                    <button
                      type="button"
                      className="btn-link"
                      disabled={isLoading}
                      onClick={() => {
                        setStep("email");
                        setCode("");
                        setNewPassword("");
                        setConfirmPassword("");
                        setError(null);
                      }}
                    >
                      Resend code
                    </button>
                  </div>
                </form>
              )}

              <div className="login-footer">
                <p>By continuing, you agree to our Terms of Service and Privacy Policy.</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}
